import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ShoppingCart, Trash2, ExternalLink, Package } from 'lucide-react';
import './CartPage.css';

const CATEGORY_LABELS = {
  notes: 'Notes', gaming: 'Gaming', clothing: 'Clothing',
  electronics: 'Electronics', books: 'Books',
  sports: 'Sports', food: 'Food', other: 'Other',
};

export default function CartPage() {
  const [cart,    setCart]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    api.get('/users/cart')
      .then(({ data }) => setCart(data.cart.filter(c => c.product)))
      .catch(() => toast.error('Could not load cart.'))
      .finally(() => setLoading(false));
  }, []);

  const handleRemove = async (productId) => {
    setRemoving(productId);
    try {
      await api.delete(`/users/cart/${productId}`);
      setCart(prev => prev.filter(c => c.product._id !== productId));
      toast.success('Removed from cart.');
    } catch {
      toast.error('Could not remove.');
    } finally {
      setRemoving(null);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  return (
    <div className="cart-page fade-in">
      <div className="cart-header">
        <h2>My Cart</h2>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 4 }}>
          {cart.length} item{cart.length !== 1 ? 's' : ''} saved
        </p>
      </div>

      {cart.length === 0 ? (
        <div className="empty-state card" style={{ padding: 'var(--space-12)' }}>
          <div className="empty-state-icon">🛒</div>
          <h4>Your cart is empty</h4>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Browse the marketplace and add items to compare
          </p>
          <Link to="/app/marketplace" className="btn btn-primary" style={{ marginTop: 8 }}>
            Browse Marketplace
          </Link>
        </div>
      ) : (
        <div className="cart-list">
          {cart.map(({ product, addedAt }) => (
            <div key={product._id} className="cart-item card">

              {/* Image */}
              <div className="cart-item-img">
                {product.images?.[0]
                  ? <img src={product.images[0]} alt={product.title} loading="lazy" />
                  : <div className="cart-item-no-img"><Package size={24} /></div>
                }
              </div>

              {/* Info */}
              <div className="cart-item-info">
                <div className="cart-item-top">
                  <span className={`badge badge-${product.category}`}>
                    {CATEGORY_LABELS[product.category] || product.category}
                  </span>
                  {product.status !== 'active' && (
                    <span className="badge badge-danger">Removed</span>
                  )}
                </div>

                <h4 className="cart-item-title">{product.title}</h4>

                <p className="cart-item-desc text-muted">
                  {product.description?.slice(0, 100)}
                  {product.description?.length > 100 ? '…' : ''}
                </p>

                <div className="cart-item-footer">
                  <span className="cart-item-price">₹{product.price?.toLocaleString()}</span>
                  <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                    Added {new Date(addedAt).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short',
                    })}
                  </span>
                </div>

                {/* Seller */}
                <p className="text-muted" style={{ fontSize: '0.78rem' }}>
                  Seller: {product.sellerSnapshot?.name} ·{' '}
                  {product.sellerSnapshot?.sellCount ?? 0} sells
                </p>
              </div>

              {/* Actions */}
              <div className="cart-item-actions">
                <Link
                  to={`/product/${product._id}`}
                  className="btn btn-primary btn-sm"
                  title="View product"
                >
                  <ExternalLink size={14} />
                  View
                </Link>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemove(product._id)}
                  disabled={removing === product._id}
                  title="Remove from cart"
                >
                  {removing === product._id
                    ? <div className="spinner" style={{ width: 14, height: 14 }} />
                    : <Trash2 size={14} />
                  }
                </button>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}